import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import {
  IconButton,
  MenuItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Theme,
  createStyles,
  Grid,
  Select,
  InputAdornment,
  Popover,
  CircularProgress,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { Autocomplete } from '@material-ui/lab';
import NumberFormat from 'react-number-format';
import ExpandMoreIcon from '../../../../static/ExpandMoreIcon.svg';
import AccountIcon from '../../../../static/Account.svg';
import CustomerIcon from '../../../../static/Customer.svg';
import PhoneIcon from '../../../../static/Phone.svg';
import colors from '../../../../themes/main-colors';
import {
  OutageLookupParams,
  OutageLookUpType,
} from '../../../../__generated__/pge-types';
import { getFromStorage, setInStorage } from '../../../../util/storage-utils';
import {
  displayPhoneNumberFormat,
  parsePhoneNumber,
} from '../../../../util/format';
import { TransitionContext } from '../../../../providers/TransitionProvider';
import { useIsMobile } from '../../../../util/style-utils';
import { useTranslation } from '../../../../hooks/useTranslation';
import AccountTextField from '../../../account-text-field';
import PhoneTextField from '../../../phone-text-field';
import TextField from '../../../text-field/TextField';
import {
  OutageMarkerView,
  useOutageLeftPanelState,
} from '../../providers/OutageLeftPanelStateProvider';
import {
  ResponsivePageView,
  useOutageResponsivePageTracker,
} from '../../providers/OutageResponsivePageTracker';
import {
  Props,
  SearchServiceAddress,
  SearchServiceAddressRequest,
  SearchServiceAddressResponse,
} from './types';
import { useQuery } from '@apollo/react-hooks';
import { SEARCH_SERVICE_ADDRESS_QUERY } from './searchPremiseQuery';
import { useOutageLookUp } from '../../hooks/useOutageLookUp';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    headerBody: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      background: colors.white,
      borderRadius: 5,
    },
    selectionDropdown: {
      height: '100%',
      [theme.breakpoints.down('sm')]: {
        borderRight: 0,
        paddingRight: theme.spacing(1),
      },
    },
    inputData: {
      width: '100%',
      paddingLeft: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        paddingLeft: 0,
      },
    },
    selectTagStyles: {
      height: '100%',
      color: colors.brightBlue,
      fontSize: '1rem',
      lineHeight: '1.25rem',
      [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(1.25),
      },
    },
    listItems: {
      color: colors.brightBlue,
      fontSize: '1rem',
      lineHeight: '1.25rem',
    },
    listItemIcon: {
      minWidth: 40,
      '& .a': { fill: colors.brightBlue, opacity: 1 },
    },
    arrowDownIcon: {
      color: colors.brightBlue,
      [theme.breakpoints.down('sm')]: { right: 26 },
    },
    selectControl: {
      paddingLeft: theme.spacing(2),
      marginRight: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        marginRight: 0,
        paddingLeft: 0,
      },
      '&:focus': { backgroundColor: colors.white },
    },
    backIconMob: {
      color: colors.aluminiumColor,
      fontSize: '.75rem',
      marginRight: theme.spacing(0.625),
    },
    lookUpIcon: { '& .a': { fill: colors.brightBlue, opacity: 1 } },
    locationSearchIconInActive: { color: colors.midGrey },
    locationSearchIconActive: { color: colors.brightBlue, opacity: 1 },
    expandMoreIconStyle: {
      position: 'absolute',
      right: 0,
      pointerEvents: 'none',
    },
    header: {
      width: '400px',
      maxWidth: '400px',
      [theme.breakpoints.down('sm')]: {
        maxWidth: 'none',
        padding: '5px',
        border: `1px solid ${colors.lightGray1}`,
        borderRadius: 5,
        width: '100%',
      },
    },
  }),
);

const useStylesAutoComplete = makeStyles((theme: Theme) =>
  createStyles({
    groupLabel: {
      '&.MuiAutocomplete-groupLabel': {
        top: 0,
        color: colors.noirBlur,
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: '1.125rem',
        position: 'absolute',
        width: '100%',
        left: 0,
        paddingLeft: theme.spacing(2.5),
        marginTop: theme.spacing(2.5),
      },
    },
    groupUl: {
      '&.MuiAutocomplete-groupUl .MuiAutocomplete-option': {
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25),
        paddingLeft: 0,
        minHeight: 0,
      },
    },
    listbox: {
      '&.MuiAutocomplete-listbox': {
        paddingLeft: theme.spacing(2.5),
        color: colors.brightBlue,
        fontSize: '1rem',
        lineHeight: '1.125rem',
        marginTop: theme.spacing(4.375),
      },
    },
    inputRoot: {
      backgroundColor: colors.white,
      '&[class*="MuiFilledInput-root"]': { paddingTop: 0, paddingLeft: 0 },
      '&[class*="MuiFilledInput-root"] .MuiAutocomplete-input': {
        padding: '5px 0px',
      },
      '&[class*="MuiInput-root"] .MuiAutocomplete-input:first-child': {
        paddingTop: 0,
      },
      '&.MuiFilledInput-root:hover': { backgroundColor: colors.white },
      '&.MuiFilledInput-root.Mui-focused': { backgroundColor: colors.white },
    },
  }),
);

const PhoneNumberFormat = (props: any) => {
  const { inputRef, onChange, ...other } = props;
  return (
    <NumberFormat
      data-testid="phone-input"
      {...other}
      getInputRef={inputRef}
      onPaste={() => window.getSelection()?.collapseToStart()}
      onValueChange={values => {
        if (values.value !== other.value) {
          onChange({
            target: { value: values.value, name: props.name },
          });
        }
      }}
      type="tel"
      format="(###) ###-####"
      mask="_"
      allowNegative={false}
      thousandSeparator
      isNumericString
    />
  );
};

export const moveCursorToEnd = (
  element: React.RefObject<HTMLInputElement>,
  inputValue?: string,
  len?: number,
) => {
  const input = element?.current;
  const value = inputValue
    ? parsePhoneNumber(inputValue)
    : input && input.value && parsePhoneNumber(input.value);
  const length = len ?? 20;
  if (input && value) {
    if (input.setSelectionRange) {
      input.focus();
      input.setSelectionRange(length, length);
    } else if ((input as any).createTextRange) {
      const t = (input as any).createTextRange();
      t.collapse(true);
      t.moveEnd('character', length);
      t.moveStart('character', length);
      t.select();
    }
  }
};

const OutageSearchBar: FC<Props> = ({ isSearchVisible, setFormData }) => {
  const classes = useStyles();
  const autoCompleteStyles = useStylesAutoComplete();
  const [lookUpValue, setLookUpValue] = useState('');
  const [open, setOpen] = useState(false);
  const [searchList, setSearchList] = useState<string[]>([]);
  const [lookUpType, setLookUpType] = useState(OutageLookUpType.Phone);
  const [iconType, setIconType] = useState(<PhoneIcon />);
  const [autoSelectOpen, setAutoSelectOpen] = useState(false);
  const [premiseDetails, setPremiseDetails] = useState<{
    encryptedValue: string | undefined;
    geoLocationToken: string | undefined;
  }>();
  const [debouncedSearchString, setDebouncedSearchString] = useState<string>(
    '',
  );
  const { wrapWithLoader } = useWrapWithLoader();
  const { setState: updateTransition } = useContext(TransitionContext);
  const {
    getOutageLookUpQueryResponse,
    setErrorNotification,
  } = useOutageLookUp();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const selectRef = useRef<any>();
  const autoCompleteInput = useRef<HTMLInputElement>(null);
  const isBrowser = typeof window !== 'undefined';
  const { setPage } = useOutageResponsivePageTracker();
  const [serviceAddresses, setServiceAddresses] = useState<
    SearchServiceAddress[] | undefined
  >();
  const {
    multiPremiseInfo,
    setOutageMarkerView,
    outageMarkerView,
    accountNotFoundInfo,
    setAccountNotFoundInfo,
    setMultiPremiseInfo,
    setSinglePremiseInfo,
    isLookUpCompleted,
  } = useOutageLeftPanelState();
  const recentPhoneSearches = 'recentPhoneSearches';
  const recentAccountSearches = 'recentAccountSearches';

  const { loading: premiseLoading, data: premiseData } = useQuery<
    { searchServiceAddress: SearchServiceAddressResponse },
    { payload: SearchServiceAddressRequest }
  >(SEARCH_SERVICE_ADDRESS_QUERY, {
    variables: { payload: { match: debouncedSearchString } },
    fetchPolicy: 'network-only',
    skip:
      debouncedSearchString.length < 3 ||
      lookUpType !== OutageLookUpType.Premise,
  });

  const premiseOptions = React.useMemo(() => {
    if (premiseLoading || !premiseData?.searchServiceAddress?.addresses) {
      return [];
    }
    setServiceAddresses(premiseData?.searchServiceAddress?.addresses);
    const formattedAddresses = premiseData.searchServiceAddress.addresses.map(
      (addr: SearchServiceAddress) =>
        [addr.addressLine1?.trim(), addr.city?.trim(), addr.postal?.trim()]
          .filter(Boolean)
          .join(', '),
    );
    return formattedAddresses
      .filter((addr: string) =>
        addr.toLowerCase().includes(lookUpValue.toLowerCase()),
      )
      .sort((a: string, b: string) => a.localeCompare(b));
  }, [premiseData, premiseLoading, lookUpValue]);

  useEffect(() => {
    if (lookUpType === OutageLookUpType.Phone) {
      const data = getFromStorage(recentPhoneSearches);
      setSearchList(data ? JSON.parse(data) : []);
    } else if (lookUpType === OutageLookUpType.Account) {
      const data = getFromStorage(recentAccountSearches);
      setSearchList(data ? JSON.parse(data) : []);
    } else {
      setSearchList([]);
    }
  }, [lookUpType, setSearchList]);

  useEffect(() => {
    setPremiseDetails({
      encryptedValue: undefined,
      geoLocationToken: undefined,
    });

    const addressValues = lookUpValue?.split(',')?.map(val => val?.trim());
    const selectedPremise = serviceAddresses?.filter(
      address =>
        address.addressLine1 === addressValues?.[0] &&
        address.city === addressValues?.[1] &&
        address.postal === addressValues?.[2],
    );

    const encryptedPremiseId = selectedPremise?.[0]?.premiseIds?.[0];
    const geoLocationToken = selectedPremise?.[0]?.geoLocationToken;

    setPremiseDetails({
      encryptedValue: encryptedPremiseId,
      geoLocationToken,
    });

    if (encryptedPremiseId) {
      setFormData &&
        setFormData({
          lookupType: lookUpType,
          encryptedValue: encryptedPremiseId,
          geoLocationToken,
        });
    }
  }, [serviceAddresses]);

  useEffect(() => {
    if (lookUpType !== OutageLookUpType.Premise) {
      return;
    }
    if (lookUpValue === '') {
      setPremiseDetails({
        encryptedValue: undefined,
        geoLocationToken: undefined,
      });
      setFormData &&
        setFormData({
          lookupType: lookUpType,
          value: '',
          encryptedPremiseId: undefined,
          encryptedValue: undefined,
          geoLocationToken: undefined,
        });
    }
    const handler = setTimeout(() => {
      if (lookUpValue.length >= 3) {
        setDebouncedSearchString(lookUpValue);
      } else {
        setDebouncedSearchString('');
      }
    }, 250);
    return () => {
      clearTimeout(handler);
    };
  }, [lookUpValue, lookUpType]);

  useEffect(() => {
    if (!isSearchVisible && setFormData && lookUpValue) {
      setFormData(getRequestParams());
    }
  }, [lookUpType, lookUpValue]);
  useEffect(() => {
    setFormData &&
      setFormData({
        lookupType: lookUpType,
        value: '',
        encryptedPremiseId: undefined,
        encryptedValue: undefined,
        geoLocationToken: undefined,
      });
  }, [lookUpType]);

  useEffect(() => {
    if (isLookUpCompleted) {
      getRequestParams();
    }
  }, [isLookUpCompleted]);

  useEffect(() => {
    if (!outageMarkerView) {
      setLookUpValue('');
      setLookUpType(OutageLookUpType.Phone);
      setIconType(<PhoneIcon />);
      setDebouncedSearchString('');
    }
  }, [outageMarkerView]);

  const showError = () => {
    setErrorNotification(true);
    updateTransition({ open: false });
    if (isBrowser) {
      window.scrollTo(0, 0);
    }
  };

  const handleSelection = (newType: OutageLookUpType, newText: any) => {
    if (lookUpType !== newType && lookUpValue) {
      setLookUpValue('');
    }
    setLookUpType(newType);
    setIconType(newText);
    setOpen(false);
  };

  const handleAutoSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAutoSelectOpen(true);
    if (open) {
      setOpen(false);
    }
    setLookUpValue(e.target.value);
  };

  const handleBlur = () => autoCompleteInput.current?.blur();

  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    const value = e.clipboardData.getData('text/plain');
    if (
      lookUpType === OutageLookUpType.Account &&
      value &&
      parsePhoneNumber(value).length === 10
    ) {
      setLookUpValue(parsePhoneNumber(value));
      moveCursorToEnd(autoCompleteInput);
    }
  };

  const handleCopyCut = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.clipboardData.setData('text/plain', e.currentTarget.value);
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setLookUpValue(parsePhoneNumber(e.target.value));
    moveCursorToEnd(autoCompleteInput, parsePhoneNumber(e.target.value));
  };

  const handleAutoSelectClick = () => {
    if (!autoSelectOpen && lookUpType !== OutageLookUpType.Premise) {
      setAutoSelectOpen(true);
    }
    if (open) {
      setOpen(false);
    }
  };

  const isHeaderSearchDisabled = () => {
    if (lookUpType !== OutageLookUpType.Premise) {
      return !(lookUpValue && lookUpValue.length === 10);
    }
    if (lookUpType === OutageLookUpType.Premise) {
      return !(
        lookUpValue &&
        premiseDetails?.encryptedValue &&
        premiseDetails.geoLocationToken
      );
    }
  };

  const renderOptionDisplay = (value: string) =>
    lookUpType === OutageLookUpType.Phone
      ? displayPhoneNumberFormat(value)
      : value;
  const getRequestParams = () => {
    const key =
      lookUpType === OutageLookUpType.Phone
        ? recentPhoneSearches
        : recentAccountSearches;
    const dataList: string[] = getFromStorage(key)
      ? JSON.parse(getFromStorage(key)!)
      : [];
    if (
      isSearchVisible &&
      !dataList.includes(lookUpValue) &&
      lookUpType !== OutageLookUpType.Premise
    ) {
      lookUpValue?.length !== 0 && dataList.push(lookUpValue);
      setInStorage(key, JSON.stringify(dataList));
      setSearchList(dataList);
    }
    if (
      !isSearchVisible &&
      isLookUpCompleted &&
      !dataList.includes(lookUpValue) &&
      lookUpType !== OutageLookUpType.Premise
    ) {
      lookUpValue?.length !== 0 && dataList.push(lookUpValue);
      setInStorage(key, JSON.stringify(dataList));
      setSearchList(dataList);
    }
    let params: OutageLookupParams = {
      lookupType: OutageLookUpType.Phone,
    };

    if (lookUpType !== OutageLookUpType.Premise) {
      params = {
        lookupType: lookUpType,
        value:
          lookUpType === OutageLookUpType.Phone
            ? displayPhoneNumberFormat(lookUpValue)
            : lookUpValue,
        encryptedValue: undefined,
        geoLocationToken: undefined,
      };
    }

    if (lookUpType === OutageLookUpType.Premise) {
      params = {
        lookupType: OutageLookUpType.Premise,
        value: undefined,
        encryptedValue: premiseDetails?.encryptedValue,
        geoLocationToken: premiseDetails?.geoLocationToken,
      };
    }
    return params;
  };

  const isSubmitDisabled = () => {
    if (!lookUpValue) {
      return true;
    }
    if (lookUpType === OutageLookUpType.Premise) {
      return lookUpValue.length < 5;
    }
    return lookUpValue.length !== 10;
  };

  const handleSubmit = wrapWithLoader(async (e: React.FormEvent) => {
    e.preventDefault();
    autoCompleteInput.current?.blur();
    if (open) {
      setOpen(false);
    }
    if (autoSelectOpen) {
      setAutoSelectOpen(false);
    }
    if (!isSubmitDisabled()) {
      const params = getRequestParams();
      try {
        const response = await getOutageLookUpQueryResponse(params);
        if (
          response &&
          response.isEligibleToReport !== null &&
          response.isEligibleToReport !== undefined
        ) {
          if (
            !response.isAccountExists ||
            (response.isAccountExists &&
              !response?.houseNumber &&
              !response.multiplePremiseList)
          ) {
            const currentAccountNotFound = accountNotFoundInfo;
            setAccountNotFoundInfo({
              ...currentAccountNotFound,
              accountNotFoundModalOpen: true,
              lookupType: params.lookupType,
              lookupValue: params.value || undefined,
            });
          } else if (
            response.isAccountExists &&
            response?.houseNumber &&
            !response.multiplePremiseList
          ) {
            if (isMobile) {
              setPage(ResponsivePageView.Details);
            }
            const houseNumber = response?.houseNumber ?? '';
            const premiseInfo = response?.outages?.[0];
            setMultiPremiseInfo(undefined);
            setSinglePremiseInfo({
              houseNumber,
              premiseDetails: premiseInfo,
              encryptedAccountNumber:
                response?.encryptedAccountNumber ?? undefined,
              encryptedServicePointList: response?.outages
                ?.map(outage => outage.encryptedServicePointId)
                .filter((id): id is string => !!id),
              selectedPremiseEncryptedValue:
                response?.encryptedPremiseId ?? undefined,
            });

            if (response?.outages?.[0]?.hasKnownOutage) {
              setOutageMarkerView(OutageMarkerView.SearchedLocation);
            } else {
              setOutageMarkerView(OutageMarkerView.NoOutage);
            }
          } else {
            if ((response?.multiplePremiseList?.length ?? 0) > 1) {
              setMultiPremiseInfo({
                ...multiPremiseInfo,
                isMultiPremiseAccount: true,
                MultiPremiseList: response.multiplePremiseList,
                isMultiSelectModalOpen: true,
                selectedPremiseDetails:
                  multiPremiseInfo?.selectedPremiseDetails,
                selectedPremiseEncryptedValue:
                  multiPremiseInfo?.selectedPremiseEncryptedValue ?? '',
              });
            }
          }
        } else {
          showError();
        }
      } catch {
        showError();
      }
    }
  });

  return (
    <>
      <Grid className={classes.header}>
        <form className={classes.headerBody} onSubmit={handleSubmit}>
          <Grid className={classes.selectionDropdown}>
            <Select
              ref={selectRef}
              value={lookUpType}
              displayEmpty
              open={false}
              IconComponent={() => (
                <ExpandMoreIcon className={classes.expandMoreIconStyle} />
              )}
              data-testid="select-test-id"
              inputProps={{ id: 'select', 'data-testid': 'select-id' }}
              disableUnderline
              className={classes.selectTagStyles}
              classes={{
                root: classes.selectControl,
                icon: classes.arrowDownIcon,
              }}
              onOpen={() => setOpen(true)}
            >
              <MenuItem value={lookUpType}>{iconType}</MenuItem>
            </Select>
            <Popover
              id="popover-select-lookUpType"
              open={open}
              anchorEl={selectRef.current}
              onClose={() => handleSelection(lookUpType, iconType)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {[
                {
                  type: OutageLookUpType.Phone,
                  icon: <PhoneIcon />,
                  text: <PhoneIcon />,
                  primary: isMobile ? t('SEARCH_BY_PHONE') : t('PHONE_NUMBER'),
                },
                {
                  type: OutageLookUpType.Account,
                  icon: <CustomerIcon />,
                  text: <CustomerIcon />,
                  primary: isMobile
                    ? t('SEARCH_BY_ACCOUNT')
                    : t('ACCOUNT_NUMBER'),
                },
                {
                  type: OutageLookUpType.Premise,
                  icon: <AccountIcon />,
                  text: <AccountIcon />,
                  primary: isMobile ? t('SEARCH_BY_ADDRESS') : t('ADDRESS'),
                },
              ].map(option => (
                <MenuItem
                  key={option.type}
                  value={option.type}
                  onClick={() => handleSelection(option.type, option.text)}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    {option.icon}
                  </ListItemIcon>
                  <ListItemText
                    className={classes.listItems}
                    primary={option.primary}
                  />
                </MenuItem>
              ))}
            </Popover>
          </Grid>
          <Grid className={classes.inputData}>
            <Autocomplete
              id="autocomplete-lookUpValue"
              data-testid="autocomplete-search"
              freeSolo={lookUpType !== OutageLookUpType.Premise}
              classes={autoCompleteStyles}
              // PopperComponent={Pop}
              options={
                lookUpType === OutageLookUpType.Premise
                  ? premiseOptions
                  : searchList
              }
              noOptionsText={t('OPTION_NOT_AVAILABLE')}
              loading={
                lookUpType === OutageLookUpType.Premise ? premiseLoading : false
              }
              open={autoSelectOpen}
              value={lookUpValue}
              onChange={(_, newValue) => {
                setLookUpValue(newValue || '');
                moveCursorToEnd(autoCompleteInput);
              }}
              onBlur={handleBlur}
              openOnFocus
              selectOnFocus
              onClose={() => {
                if (lookUpValue.length === 0) {
                  setFormData &&
                    setFormData({ lookupType: lookUpType, value: '' });
                }
                setAutoSelectOpen(false);
                moveCursorToEnd(autoCompleteInput, lookUpValue);
              }}
              style={{ width: '100%' }}
              groupBy={() =>
                lookUpType === OutageLookUpType.Premise
                  ? t('SELECT_YOUR_ADDRESS')
                  : t('RECENT_SEARCHES')
              }
              renderOption={renderOptionDisplay}
              renderInput={params =>
                lookUpType === OutageLookUpType.Phone ? (
                  <PhoneTextField
                    {...params}
                    label=""
                    inputRef={autoCompleteInput}
                    variant="filled"
                    fullWidth
                    autoComplete="telephone"
                    placeholder={t('SEARCH_BY_PHONE_NUMBER')}
                    onClick={handleAutoSelectClick}
                    onChange={handleAutoSelectChange}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      inputComponent: PhoneNumberFormat as any,
                    }}
                    margin="none"
                    onPaste={handlePaste}
                    onCopy={handleCopyCut}
                    onCut={handleCopyCut}
                    onFocus={handleInputFocus}
                  />
                ) : lookUpType === OutageLookUpType.Account ? (
                  <AccountTextField
                    {...params}
                    label=""
                    inputRef={autoCompleteInput}
                    inputProps={{
                      ...params.inputProps,
                      maxLength: 10,
                      minLength: 10,
                      type: 'tel',
                    }}
                    variant="filled"
                    fullWidth
                    placeholder={t('SEARCH_BY_ACCOUNT_NUMBER')}
                    onClick={handleAutoSelectClick}
                    onChange={handleAutoSelectChange}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                    }}
                    margin="none"
                    onPaste={handlePaste}
                    onCopy={handleCopyCut}
                    onCut={handleCopyCut}
                    onFocus={handleInputFocus}
                  />
                ) : (
                  <TextField
                    label=""
                    inputRef={autoCompleteInput}
                    margin="none"
                    fullWidth
                    onClick={handleAutoSelectClick}
                    onChange={handleAutoSelectChange}
                    {...params}
                    placeholder={t('SEARCH_BY_ADDRESS')}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      endAdornment: (
                        <>
                          {premiseLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    inputProps={{
                      ...params.inputProps,
                    }}
                    variant="filled"
                  />
                )
              }
            />
          </Grid>
          {isSearchVisible && (
            <Grid>
              <IconButton
                aria-label="location-search-icon"
                disabled={isHeaderSearchDisabled()}
                type="submit"
              >
                <SearchIcon
                  className={
                    isHeaderSearchDisabled()
                      ? classes.locationSearchIconInActive
                      : classes.locationSearchIconActive
                  }
                />
              </IconButton>
            </Grid>
          )}
        </form>
      </Grid>
    </>
  );
};

export default OutageSearchBar;
